/**
 * Redacción de rutas (contrato 3.1, `redaction`): se aplica a `path`,
 * `entry_path`, la ruta del `referrer` y `api_slow.endpoint` antes de que
 * salgan del navegador. L5FrontEvent y el CRM vuelven a redactar, pero el front
 * es el primero: el enlace de acceso `/login/<otp>` es la credencial de la
 * cuenta (no se anula al usarlo) y no puede viajar en claro.
 *
 * Reglas:
 * - `/login/<algo>` → `/login/:otp`; `/login/<algo>/<destino>` →
 *   `/login/:otp/<destino>` si el destino es una palabra (`^[a-z][a-z-]{0,30}$`),
 *   si no `/login/:otp/:next`.
 * - Cualquier segmento que sea un ULID o un UUID, tenga 20 caracteres o más,
 *   lleve `@` o tenga 6 dígitos o más → `:id`.
 * - Sin query string ni fragmento.
 */

const ULID = /^[0-9A-HJKMNP-TV-Z]{26}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LOGIN_NEXT = /^[a-z][a-z-]{0,30}$/;

const decode = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const digitCount = (value: string): number => (value.match(/\d/g) || []).length;

/** Un segmento de ruta que puede identificar a alguien (id, email, teléfono, token…). */
export const isSensitiveSegment = (segment: string): boolean => {
  if (!segment) return false;
  const values = [segment, decode(segment)];
  return values.some(
    (value) =>
      ULID.test(value) ||
      UUID.test(value) ||
      value.length >= 20 ||
      value.indexOf("@") >= 0 ||
      digitCount(value) >= 6
  );
};

const generic = (segment: string): string => (isSensitiveSegment(segment) ? ":id" : segment);

/** Ruta redactada, empezando por `/` y sin query string ni fragmento. */
export const redactPath = (raw: string | null | undefined): string => {
  let path = String(raw ?? "").split("#")[0].split("?")[0];
  if (path.charAt(0) !== "/") path = `/${path}`;
  const segments = path.split("/");
  const isLogin = (segments[1] || "").toLowerCase() === "login";
  return segments
    .map((segment, i) => {
      if (i === 0 || segment === "") return segment;
      if (isLogin && i === 2) return ":otp";
      if (isLogin && i === 3) return LOGIN_NEXT.test(segment) ? segment : ":next";
      return generic(segment);
    })
    .join("/");
};

/** URL sin query string ni fragmento y con la ruta redactada; null si no es una URL. */
export const redactUrl = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    // Esquemas no especiales (android-app://…) dan origin "null".
    const origin = url.origin && url.origin !== "null" ? url.origin : `${url.protocol}//${url.host}`;
    return `${origin}${redactPath(url.pathname)}`;
  } catch {
    return null;
  }
};
